import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, Divider, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import store from "../../redux/store";
import Tabs from "../../components/tabs/tabs";
import bankServices from "../../services/bankServices";
import walletServices from "../../services/walletServices";
import SummaryTab from "./components/SummaryTab";
import FrozenTab from "./components/FrozenTab";
import LedgerTab from "./components/LedgerTab";
import WithdrawTab from "./components/WithdrawTab";
import WalletSummaryCard from "./components/WalletSummaryCard";
import AddFundsModal from "./components/AddFundsModal";
import { getWalletMetrics } from "./utils/walletMetrics";
import logsService from "../../services/logServices";
import InvestorActivityEventType from "../../utils/eventTypes";

export default function Wallet() {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [frozenPage, setFrozenPage] = useState(1);
  const [frozenPageSize, setFrozenPageSize] = useState(20);
  const [frozen, setFrozen] = useState({ items: [], page: 1, pageSize: 20, total: 0 });
  const [frozenLoading, setFrozenLoading] = useState(false);

  const [ledgerType, setLedgerType] = useState("");
  const [poolFilter, setPoolFilter] = useState("");
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerStatus, setLedgerStatus] = useState("");
  const [ledger, setLedger] = useState({ items: [], page: 1, pageSize: 5, total: 0, totalCredit: 0, totalDebit: 0 });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  // DataGrid pagination is 0-based; the API is 1-based.
  const [ledgerPaginationModel, setLedgerPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawBankAccountId, setWithdrawBankAccountId] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [netReceivable, setNetReceivable] = useState(0);

  const [addFundsOpen, setAddFundsOpen] = useState(false);

  const investorId = store.getState().user?.data?._id;

  const loadSummary = async () => {
    setSummaryLoading(true);
    const res = await walletServices.getWalletSummary();
    setSummaryLoading(false);
    if (res?.freeBalance == null && !res?.success) {
      enqueueSnackbar(res?.data?.message || "Failed to load wallet summary", { variant: "error" });
      return;
    }
    setSummary(res);
  };

  const loadFrozen = async () => {
    setFrozenLoading(true);
    const res = await walletServices.getWalletFrozen({ page: frozenPage, pageSize: frozenPageSize });
    setFrozenLoading(false);
    if (!res || res?.success === false) {
      enqueueSnackbar(res?.message || "Failed to load frozen transactions", { variant: "error" });
      return;
    }
    setFrozen(res);
  };

  const loadLedger = async () => {
    setLedgerLoading(true);
    const res = await walletServices.getWalletLedger({
      type: ledgerType || undefined,
      pool: poolFilter || undefined,
      search: ledgerSearch.trim() || undefined,
      status: ledgerStatus || undefined,
      page: ledgerPaginationModel.page + 1,
      pageSize: ledgerPaginationModel.pageSize,
    });
    setLedgerLoading(false);
    if (!res || res?.success === false) {
      enqueueSnackbar(res?.message || "Failed to load wallet ledger", { variant: "error" });
      return;
    }
    // Support both top-level and nested ({ data: { items } }) response shapes.
    const payload = Array.isArray(res?.data?.items) ? res.data : res;
    setLedger({
      items: payload?.items || [],
      page: payload?.page || 1,
      pageSize: payload?.pageSize || ledgerPaginationModel.pageSize,
      total: payload?.total || 0,
      totalCredit: payload?.totalCredit || 0,
      totalDebit: payload?.totalDebit || 0,
    });
  };

  const handleChangeLedgerType = (value) => {
    setLedgerType(value);
    setLedgerPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleChangeLedgerSearch = (value) => {
    setLedgerSearch(value);
    setLedgerPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleChangeLedgerStatus = (value) => {
    setLedgerStatus(value);
    setLedgerPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleChangePoolFilter = (value) => {
    setPoolFilter(value);
    setLedgerPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  useEffect(() => {
    if (activeTab === 2) loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, ledgerType, poolFilter, ledgerSearch, ledgerStatus, ledgerPaginationModel.page, ledgerPaginationModel.pageSize]);

  const loadBankAccounts = async () => {
    if (!investorId) return;
    const res = await bankServices.getBankAccountByStatus(investorId, "APPROVED");
    if (!res || res?.success === false) return;
    const list = Array.isArray(res.data) ? res.data : [];
    const sorted = [...list].sort((a, b) => (b?.isPrimary ? 1 : 0) - (a?.isPrimary ? 1 : 0));
    setBankAccounts(sorted);
    if (!withdrawBankAccountId) {
      const primary = sorted.find((x) => x?.isPrimary);
      if (primary?._id) setWithdrawBankAccountId(primary._id);
    }
  };

  useEffect(() => {
    loadSummary();
    loadBankAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId]);

  useEffect(() => {
    if (activeTab === 1) loadFrozen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, frozenPage, frozenPageSize]);

  const frozenRows = useMemo(() => {
    const items = Array.isArray(frozen?.items) ? frozen.items : [];
    const baseUrl = typeof frozen?.url === "string" ? frozen.url : "";

    return items.map((x) => {
      const docUrl = x?.image?.docUrl;
      const unitTitle = x?.title;
      const projectName = x?.projectName;

      return {
        ...x,
        _title: unitTitle,
        _propertyType: x?.propertyType,
        _tenancyType: x?.tenancyType,
        _imageUrl: docUrl ? `${baseUrl}${docUrl}` : "",
        _propertyCode: x?.propertyCode || x?.data?.propertyCode || "",
        _status: x?.status || "Frozen",
        _statusLabel: x?.statusMessage || x?.stage || x?.stageLabel || "",
      };
    });
  }, [frozen]);

  const walletMetrics = useMemo(() => getWalletMetrics(summary), [summary]);

  const ledgerRows = useMemo(() => {
    const items = Array.isArray(ledger?.items) ? ledger.items : [];
    return items.map((x, index) => ({
      ...x,
      id: x?._id,
      createdAt: x?.createdAt ?? x?.created_at,
      sourceTransactionId: x?.sourceRef?.transactionId || "-",
      sourceListingId: x?.sourceRef?.listingId || "-",
      totalDebit: x?.totalDebit,
      totalCredit: x?.totalCredit,
    }));
  }, [ledger]);

  const onWithdraw = async () => {
    const amount = Number(withdrawAmount);
    const available = getWalletMetrics(summary).available;
    if (!Number.isFinite(amount) || amount <= 0) {
      enqueueSnackbar("Enter a valid amount", { variant: "error" });
      return;
    }
    if (amount < 5000) {
      enqueueSnackbar("Minimum withdrawal amount is ₹5,000", { variant: "error" });
      return;
    }
    if (amount > available) {
      enqueueSnackbar("Amount exceeds available balance", { variant: "error" });
      return;
    }
    if (!withdrawBankAccountId) {
      enqueueSnackbar("Select a bank account", { variant: "error" });
      return;
    }
    setWithdrawing(true);
    const res = await walletServices.withdraw({ netReceivable, amount, bankAccountId: withdrawBankAccountId });
    setWithdrawing(false);
    if (!res || res?.success === false) {
      enqueueSnackbar(res?.message || "Withdrawal failed", { variant: "error" });
      return;
    }
    enqueueSnackbar("Withdrawal initiated", { variant: "success" });
    logsService.addLog({
      userId: investorId,
      event: InvestorActivityEventType.WITHDRAWAL_INITIATED
    })
    setWithdrawAmount("");
    await loadSummary();
    if (activeTab === 2) await loadLedger();
  };

  const handleAddFundsSuccess = async () => {
    await loadSummary();
    if (activeTab === 2) await loadLedger();
  };

  const tabs = [
    {
      name: "Overview",
      tab: <SummaryTab summary={summary} loading={summaryLoading} />,
    },
    {
      name: "Frozen",
      tab: <FrozenTab rows={frozenRows} />,
    },
    {
      name: "Transactions",
      tab: (
        <LedgerTab
          rows={ledgerRows}
          total={ledger?.total ?? 0}
          ledgerType={ledgerType}
          poolFilter={poolFilter}
          onChangePoolFilter={handleChangePoolFilter}
          onChangeType={handleChangeLedgerType}
          search={ledgerSearch}
          onChangeSearch={handleChangeLedgerSearch}
          statusFilter={ledgerStatus}
          onChangeStatus={handleChangeLedgerStatus}
          loading={ledgerLoading}
          paginationModel={ledgerPaginationModel}
          onPaginationModelChange={setLedgerPaginationModel}
          totalCredit={ledger?.totalCredit ?? 0}
          totalDebit={ledger?.totalDebit ?? 0}
          availableAmount={walletMetrics.available}
          frozenAmount={walletMetrics.frozen}
        />
      ),
    },
    {
      name: "Withdraw",
      tab: (
        <WithdrawTab
          summary={summary}
          bankAccounts={bankAccounts}
          withdrawAmount={withdrawAmount}
          setWithdrawAmount={setWithdrawAmount}
          withdrawBankAccountId={withdrawBankAccountId}
          setWithdrawBankAccountId={setWithdrawBankAccountId}
          withdrawing={withdrawing}
          onWithdraw={onWithdraw}
          setNetReceivable={setNetReceivable}
        />
      ),
    },
  ];

  return (
    <Box>
      <Stack spacing={0.5} sx={{ mt: { md: 5, xs: 3 }, mb: 2.5 }}>
        <Typography variant="h3" fontWeight={800}>
          Wallet
        </Typography>
      </Stack>

      <WalletSummaryCard
        summary={summary}
        loading={summaryLoading}
        onAddFunds={() => 
          {
            setAddFundsOpen(true);
            logsService.addLog({
              userId: investorId,
              event: InvestorActivityEventType.ADD_FUNDS_CLICKED
            })
          }

        }
        onWithdraw={() => setActiveTab(3)}
        onTransactions={() => setActiveTab(2)}
      />

      <AddFundsModal
        open={addFundsOpen}
        onClose={() => setAddFundsOpen(false)}
        onSuccess={handleAddFundsSuccess}
        locked={summary?.locked}
      />

      <Card sx={{ mt: 2.5, borderRadius: 3 }}>
        <Stack>
          <Box sx={{ p: "10px" }}>
            <Tabs activeTab={activeTab} tabs={tabs} onClick={setActiveTab} />
            <Divider sx={{ width: "calc(100% + 80px)" }} />
          </Box>
          <Box>{tabs[activeTab]?.tab}</Box>
        </Stack>
      </Card>
    </Box>
  );
}
